import React, { createContext, useState, useContext, useEffect } from "react";
import useAirports from "../hooks/useAirports";

// 时区选项定义
export const TIMEZONE_OPTIONS = {
    UTC: "UTC", // 世界协调时间
    BEIJING: "Asia/Shanghai", // 北京时间
    LOCAL: "LOCAL", // 机场当地时区（特殊值）
};

// 创建时区上下文
const TimeZoneContext = createContext();

// 时区状态管理Provider组件
export const TimeZoneProvider = ({ children }) => {
    const [timeZone, setTimeZone] = useState(TIMEZONE_OPTIONS.UTC);

    // 持久化存储与恢复时区偏好
    useEffect(() => {
        localStorage.setItem("preferredTimeZone", timeZone);
    }, [timeZone]);

    useEffect(() => {
        const savedTimeZone = localStorage.getItem("preferredTimeZone");
        if (savedTimeZone) setTimeZone(savedTimeZone);
    }, []);

    // 提供上下文
    return <TimeZoneContext.Provider value={{ timeZone, setTimeZone }}>{children}</TimeZoneContext.Provider>;
};

/**
 * 根据时区模式和机场代码获取实际时区标识符
 * @param {string} timeZoneMode - 时区模式（UTC/BEIJING/LOCAL）
 * @param {string} airportCode - 机场代码（仅LOCAL模式需要）
 * @param {Object} airportsMap - 机场数据映射
 * @returns {string} 实际的时区标识符
 */
export const getTimezoneByMode = (timeZoneMode, airportCode = null, airportsMap = {}) => {
    switch (timeZoneMode) {
        case TIMEZONE_OPTIONS.BEIJING:
            return "Asia/Shanghai";
        case TIMEZONE_OPTIONS.LOCAL:
            try {
                if (!airportCode || !airportsMap || !airportsMap[airportCode]) {
                    return "UTC"; // 找不到机场时，降级到UTC
                }
                return airportsMap[airportCode].timezone || "UTC";
            } catch (error) {
                console.error("获取机场时区错误:", error);
                return "UTC";
            }
        default: // UTC
            return "UTC";
    }
};

// 自定义Hook，方便组件使用时区上下文
export const useTimeZone = () => {
    const context = useContext(TimeZoneContext);
    if (!context) {
        throw new Error("useTimeZone must be used within a TimeZoneProvider");
    }

    // 添加便捷方法
    const { timeZone, setTimeZone } = context;
    
    const { airportsMap } = useAirports();

    // 判断当前是否为UTC时区
    const isUTC = timeZone === TIMEZONE_OPTIONS.UTC;

    // 判断当前是否为北京时间
    const isBeijing = timeZone === TIMEZONE_OPTIONS.BEIJING;

    // 判断当前是否为本地时区
    const isLocal = timeZone === TIMEZONE_OPTIONS.LOCAL;

    // 获取时区显示名称
    const getTimeZoneDisplayName = () => {
        switch (timeZone) {
            case TIMEZONE_OPTIONS.UTC:
                return "UTC";
            case TIMEZONE_OPTIONS.BEIJING:
                return "北京时间";
            case TIMEZONE_OPTIONS.LOCAL:
                return "当地时间";
            default:
                return timeZone;
        }
    };

    /**
     * 获取指定机场的时区
     * @param {string} airportCode - 机场代码
     * @param {Object} airportsMap - 机场数据映射
     * @returns {string} 时区标识符
     */
    const getAirportTimezone = (airportCode, airportsMap = {}) => {
        return getTimezoneByMode(timeZone, airportCode, airportsMap);
    };

    return {
        timeZone,
        setTimeZone,
        isUTC,
        isBeijing,
        isLocal,
        getTimeZoneDisplayName,
        getAirportTimezone,
        airportsMap,
    };
};

export default TimeZoneContext;
